import { useEffect, useState } from 'react'
import {
  Layout as AntLayout,
  Row,
  Col,
  Card,
  Upload,
  Button,
  message,
  Spin,
  Space,
  Empty,
  Image,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import {
  UploadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  AppstoreAddOutlined
} from '@ant-design/icons'
import MarketingServices from '../../utils/api/marketing'
import LayoutPage from '../../layouts/Main'
import './MarketingMarketplace.scss'

const { Content } = AntLayout
const { Option } = Select
const { Title, Text, Paragraph } = Typography
const { confirm } = Modal

export default function MarketingMarketplace() {
  // --- estados ----
  const [banners, setBanners] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalVisible, setModalVisible] = useState(false)
  const [editing, setEditing] = useState(null)
  const [uploadingId, setUploadingId] = useState(null)
  const [previewImage, setPreviewImage] = useState('')
  const [previewVisible, setPreviewVisible] = useState(false)
  const [form] = Form.useForm()
  const selectedSection = Form.useWatch('section', form)

  // helper para convertir a base64
  function getBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = reject
    })
  }

  // cargar banners
  const refreshLists = async () => {
    setLoading(true)
    try {
      const resp = await MarketingServices.GetAll()
      const sortedBanners = resp.data.banners.sort((a, b) => a.order - b.order)
      setBanners(sortedBanners)
    } catch (error) {
      message.error('Error al cargar los banners')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refreshLists()
  }, [])

  // abre modal para nuevo o editar
  const openModal = (banner = null) => {
    if (banner) {
      setEditing(banner)
      setTimeout(() => {
        form.setFieldsValue({
          title: banner.title,
          order: banner.order,
          section: banner.section,
          link: banner.url || '',
          fileList: []
        })
      }, 0)
    } else {
      setEditing(null)
      form.resetFields()
      const nextOrder = banners.length > 0 ? Math.max(...banners.map(b => b.order)) + 1 : 1
      setTimeout(() => {
        form.setFieldsValue({
          order: nextOrder,
          section: 'home',
          link: '',
          fileList: []
        })
      }, 0)
    }
    setModalVisible(true)
  }

  // manejar submit
  const onFinish = async (values) => {
    const { title, order, section, link, fileList } = values
    const fileItem = fileList?.[0]
    const hasImage = fileItem && fileItem.originFileObj

    if (!editing && !hasImage) {
      return message.error('La imagen es obligatoria para crear un nuevo banner')
    }

    const normalizedLink = link?.trim() || ''

    const body = {
      title: title.trim(),
      order: Number(order),
      section,
      url: normalizedLink || null,
    }

    if (hasImage) {
      try {
        body.fileBase64 = await getBase64(fileItem.originFileObj)
      } catch (e) {
        return message.error('No se pudo procesar la imagen')
      }
    }

    setUploadingId(editing?.id || 'new')

    try {
      if (editing?.id) {
        await MarketingServices.Update(editing.id, body)
        message.success('Banner actualizado correctamente')
      } else {
        await MarketingServices.Add(body)
        message.success('Banner creado correctamente')
      }
      await refreshLists()
      setModalVisible(false)
      form.resetFields()
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || 'Error procesando el banner'
      message.error(errMsg)
    } finally {
      setUploadingId(null)
    }
  }

  // eliminar banner
  const onDelete = async (id) => {
    confirm({
      title: '¿Eliminar banner?',
      icon: <ExclamationCircleOutlined />,
      content: 'Esta acción no se puede deshacer. El banner se eliminará permanentemente.',
      okText: 'Sí, eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      centered: true,
      onOk: async () => {
        try {
          await MarketingServices.Delete(id)
          message.success('Banner eliminado correctamente')
          await refreshLists()
        } catch (error) {
          message.error('Error al eliminar el banner')
        }
      }
    })
  }

  // preview de imagen
  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj)
    }
    setPreviewImage(file.url || file.preview)
    setPreviewVisible(true)
  }

  const getImageDimensions = (file) => new Promise((resolve, reject) => {
    const imageUrl = URL.createObjectURL(file)
    const image = new window.Image()

    image.onload = () => {
      resolve({ width: image.width, height: image.height })
      URL.revokeObjectURL(imageUrl)
    }

    image.onerror = () => {
      reject(new Error('No se pudieron leer las dimensiones de la imagen'))
      URL.revokeObjectURL(imageUrl)
    }

    image.src = imageUrl
  })

  const validateBannerImageBySection = async (_, fileList = []) => {
    const currentSection = form.getFieldValue('section')
    const fileItem = fileList?.[0]

    if (!fileItem?.originFileObj || !['promos', 'featured'].includes(currentSection)) {
      return Promise.resolve()
    }

    try {
      const { width, height } = await getImageDimensions(fileItem.originFileObj)
      if (width !== 656 || height !== 220) {
        return Promise.reject(new Error('Para Promociones y Destacados la imagen debe medir exactamente 656×220 px'))
      }
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(new Error('No se pudieron validar las dimensiones de la imagen'))
    }
  }

  // obtener badge de sección
  const getSectionBadge = (section) => {
    const sectionConfig = {
      home: { color: 'green', text: 'Home' },
      promos: { color: 'blue', text: 'Promos' },
      featured: { color: 'purple', text: 'Destacados' }
    }

    const config = sectionConfig[section] || { color: 'default', text: section }
    return (
      <Tag color={config.color} className="section-badge">
        {config.text}
      </Tag>
    )
  }

  // calcular estadísticas
  const getStats = () => {
    const homeBanners = banners.filter(b => b.section === 'home').length
    const promoBanners = banners.filter(b => b.section === 'promos').length
    const featuredBanners = banners.filter(b => b.section === 'featured').length

    return {
      total: banners.length,
      home: homeBanners,
      promos: promoBanners,
      featured: featuredBanners
    }
  }

  const stats = getStats()

  // renderizar card de banner
  const renderBannerCard = (banner) => (
    <Col key={banner.id} xs={24} sm={12} md={8} lg={6}>
      <Card
        className="banner-card"
        cover={
          <div style={{ position: 'relative' }}>
            <Image
              src={banner.imageUrl}
              alt={banner.title}
              className="banner-image"
              preview={{
                mask: (
                  <div style={{ color: '#fff', fontSize: '12px' }}>
                    <EyeOutlined /> Ver
                  </div>
                )
              }}
              fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlbiBubyBkaXNwb25pYmxlPC90ZXh0Pjwvc3ZnPg=="
            />
            <div style={{
              position: 'absolute',
              top: '8px',
              right: '8px'
            }}>
              {getSectionBadge(banner.section)}
            </div>
          </div>
        }
        actions={[
          <Tooltip title="Editar banner" key="edit">
            <EditOutlined onClick={() => openModal(banner)} />
          </Tooltip>,
          <Tooltip title="Eliminar banner" key="delete">
            <DeleteOutlined onClick={() => onDelete(banner.id)} />
          </Tooltip>
        ]}
      >
        <Card.Meta
          title={
            <div className="banner-title-section">
              <div className="banner-title-label">TÍTULO MOSTRADO</div>
              <Text strong ellipsis={{ tooltip: banner.title }} className="banner-title-text">
                {banner.title}
              </Text>
            </div>
          }
          description={
            <div className="banner-info">
              <div className="info-item">
                <div className="info-label">Orden de visualización</div>
                <div className="info-value">#{banner.order}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Sección mostrada</div>
                <div className={`info-value section-${banner.section}`}>
                  {banner.section === 'home' ? 'Home' :
                    banner.section === 'promos' ? 'Promociones' :
                      'Destacados'}
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">Fecha de actualización</div>
                <div className="info-value">
                  {new Date(banner.updatedAt).toLocaleDateString()}
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">Link asignado</div>
                <div className="info-value">
                  {banner.url || 'Sin link'}
                </div>
              </div>
            </div>
          }
        />
      </Card>
    </Col>
  )

  if (loading) {
    return (
      <LayoutPage>
        <div className="loading-container">
          <Spin size="large" tip="Cargando banners..." />
        </div>
      </LayoutPage>
    )
  }

  return (
    <LayoutPage>
      <div className="marketing-marketplace">
        {/* ─── BOTÓN FLOTANTE A LA DERECHA ────────────────────────────── */}
        <div className="floating-action-button">
          <Button
            type="primary"
            icon={<AppstoreAddOutlined />}
            onClick={() => openModal()}
            size="large"
            className="add-button"
          >
            Nuevo Banner
          </Button>
        </div>

        <AntLayout style={{ background: 'transparent' }}>
          <Content>
            <div className="admin-container">
              {/* ─── HEADER CON ESTADÍSTICAS ────────────────────────────── */}
              <div className="admin-header">
                <div className="header-content">
                  <div className="header-text">
                    <Title level={2} className="admin-title">
                      Administración de mercadotecnia
                    </Title>
                  </div>

                  <div className="header-stats">
                    <div className="stat-item">
                      <span className="stat-number">{stats.total}</span>
                      <span className="stat-label">Total</span>
                    </div>
                    <div className="stat-divider"></div>
                    <div className="stat-item">
                      <span className="stat-number">{stats.home}</span>
                      <span className="stat-label">Home</span>
                    </div>
                    <div className="stat-divider"></div>
                    <div className="stat-item">
                      <span className="stat-number">{stats.promos}</span>
                      <span className="stat-label">Promos</span>
                    </div>
                    <div className="stat-divider"></div>
                    <div className="stat-item">
                      <span className="stat-number">{stats.featured}</span>
                      <span className="stat-label">Destacados</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ─── LISTA DE BANNERS ──────────────────────────────────── */}
              <div className="banners-list">
                {banners.length === 0 ? (
                  <div className="empty-state">
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <Text type="secondary">
                          No hay banners configurados en el sistema
                        </Text>
                      }
                    >
                      <Button
                        type="primary"
                        size="large"
                        icon={<PlusOutlined />}
                        onClick={() => openModal()}
                        style={{ background: '#145f9d', borderColor: '#145f9d' }}
                      >
                        Crear Primer Banner
                      </Button>
                    </Empty>
                  </div>
                ) : (
                  <Row gutter={[16, 16]} className="banners-grid">
                    {banners.map(renderBannerCard)}
                  </Row>
                )}
              </div>
            </div>
          </Content>
        </AntLayout>

        {/* ─── MODAL AGREGAR/EDITAR ─────────────────────────────── */}
        {modalVisible && (
          <Modal
            className="banner-modal"
            title={editing ? 'Editar Banner' : 'Crear Nuevo Banner'}
            open={modalVisible}
            onCancel={() => {
              setModalVisible(false)
              form.resetFields()
            }}
            footer={null}
            width={600}
            destroyOnClose
            forceRender
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              requiredMark="optional"
            >
              <Form.Item
                name="title"
                label="Título del banner"
                rules={[
                  { required: true, message: 'El título es obligatorio' },
                  { min: 3, message: 'El título debe tener al menos 3 caracteres' },
                  { max: 100, message: 'El título no puede exceder 100 caracteres' }
                ]}
              >
                <Input
                  placeholder="Ej: Promoción de Verano 2024"
                  maxLength={100}
                  showCount
                />
              </Form.Item>

              <Form.Item
                name="section"
                label="Sección de visualización"
                rules={[{ required: true, message: 'La sección es obligatoria' }]}
              >
                <Select placeholder="Selecciona dónde se mostrará el banner">
                  <Option value="home">
                    <Space>
                      <Tag color="green">Home</Tag>
                      <span>Página principal del marketplace</span>
                    </Space>
                  </Option>
                  <Option value="promos">
                    <Space>
                      <Tag color="blue">Promociones</Tag>
                      <span>Sección de ofertas especiales</span>
                    </Space>
                  </Option>
                  <Option value="featured">
                    <Space>
                      <Tag color="purple">Destacados</Tag>
                      <span>Productos destacados</span>
                    </Space>
                  </Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="order"
                label="Orden de visualización"
                rules={[
                  { required: true, message: 'El orden es obligatorio' },
                  { type: 'number', min: 1, message: 'El orden debe ser mayor a 0' }
                ]}
                extra="Los banners se ordenan de menor a mayor número"
              >
                <InputNumber
                  min={1}
                  max={100}
                  style={{ width: '100%' }}
                  placeholder="1"
                />
              </Form.Item>

              <Form.Item
                name="link"
                label="Link del banner (opcional)"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value || !value.trim()) return Promise.resolve()
                      const normalizedValue = value.trim()
                      const isExternal = /^https?:\/\//i.test(normalizedValue)
                      const isInternal = normalizedValue.startsWith('/')

                      if (isExternal || isInternal) return Promise.resolve()

                      return Promise.reject(new Error('Ingresa una URL válida (https://...) o una ruta interna (/marketplace/...)'))
                    }
                  }
                ]}
                extra="Si se define, al dar clic al banner redirigirá a este destino"
              >
                <Input
                  placeholder="Ej: https://epnopartners.com/promocion o /marketplace/categoria/electrico"
                  maxLength={300}
                />
              </Form.Item>

              <Form.Item
                name="fileList"
                label="Imagen del banner"
                dependencies={['section']}
                valuePropName="fileList"
                getValueFromEvent={(e) => e?.fileList || []}
                rules={[
                  {
                    required: !editing,
                    message: 'La imagen es obligatoria para nuevos banners'
                  },
                  {
                    validator: validateBannerImageBySection
                  }
                ]}
                extra={
                  <div>
                    <Text type="secondary">
                      {`Formatos: JPG, PNG, SVG • ${['promos', 'featured'].includes(selectedSection)
                        ? 'Tamaño requerido: 656×220px'
                        : 'Tamaño requerido: 1200×400px'}`}
                      {editing && ' • Dejar vacío para mantener la imagen actual'}
                    </Text>
                  </div>
                }
              >
                <Upload
                  className="form-upload"
                  listType="picture-card"
                  accept="image/*"
                  beforeUpload={() => false}
                  maxCount={1}
                  onPreview={handlePreview}
                  onChange={({ fileList }) => {
                    form.setFieldsValue({ fileList: fileList.slice(-1) })
                  }}
                >
                  {form.getFieldValue('fileList')?.length >= 1 ? null : (
                    <div>
                      <UploadOutlined style={{ fontSize: '20px', color: '#8c8c8c' }} />
                      <div style={{ marginTop: 8, color: '#8c8c8c' }}>
                        Subir imagen
                      </div>
                    </div>
                  )}
                </Upload>
              </Form.Item>

              <div className="form-actions">
                <Space>
                  <Button
                    onClick={() => {
                      setModalVisible(false)
                      form.resetFields()
                    }}
                    disabled={!!uploadingId}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={!!uploadingId}
                    size="large"
                    style={{ background: '#145f9d', borderColor: '#145f9d' }}
                  >
                    {editing ? 'Actualizar Banner' : 'Crear Banner'}
                  </Button>
                </Space>
              </div>
            </Form>
          </Modal>
        )}

        {/* Preview de imagen */}
        <Image
          width={0}
          height={0}
          style={{ display: 'none' }}
          preview={{
            visible: previewVisible,
            src: previewImage,
            onVisibleChange: (value) => {
              setPreviewVisible(value)
            },
          }}
        />
      </div>
    </LayoutPage>
  )
}